import React, { useEffect } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import DateInput from "../../input/DateInput";
import PercentageInput from "../../input/PercentageInput";
import RateInput from "../../input/RateInput";
import RadioGroup from "../../structure/RadioGroup";
import { Taxation } from "../../enums/Taxation";
import { DisabilityEmployerFormValues } from "../../disability/DisabilityEmployerFormValues";
import { useAuth } from "oidc-react";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import { DisabilityProduct } from "../../enums/DisabilityProduct";
import { Relationship } from "../../enums/Relationship";
import { LifeEmployerFormValues } from "../../life/LifeEmployerFormValues";
import { useFormikContext } from "formik";
import { usePolicy } from "../../../hooks/usePolicy";
import { LeaveType } from "../../enums/LeaveType";
import {
    AccidentPolicyCoverage,
    HospitalIndemnityPolicyCoveragePlan,
    ShortTermDisabilityPolicyCoverage, StateAbbreviation
} from "../../../generated/graphql";
import { useWageAsOfDate } from "../../../hooks/useWageAsOfDate";
import _ from "lodash";
import { FederationProduct, findProductFor } from "../../enums/FederationProduct";
import { getKeyByValue } from "../../enums/KeyFinder";
import { ConditionType } from "../../enums/ConditionType";
import { GenericRelationship } from "../../enums/GenericRelationship";
import { PfmlEmployerFormValues } from "../../pfml/PfmlEmployerFormValues";

const Premiums = () => {
    const { values, setFieldValue } = useFormikContext<EmployerFormValues>();
    const auth = useAuth();

    const policyNumber = values.policyNumber;
    const privacyId = values.privacyId;
    const dateLastWorked = values.dateLastWorked as string;
    const disabilityProduct = (values as DisabilityEmployerFormValues)?.product as DisabilityProduct;
    const product = findProductFor(disabilityProduct) as string;
    const { data: wageAsOfDateData } = useWageAsOfDate(policyNumber, privacyId, dateLastWorked, product as FederationProduct, dateLastWorked, auth)
    const wageAsOfDate = wageAsOfDateData?.findWageAsOfDate?.wageAsOfDate
    const { data: policyData } = usePolicy(policyNumber, privacyId, wageAsOfDate, auth)
    const policy = policyData?.findPolicy

    const policyCoverages = policy?.memberGroups?.[0]?.coverages as any;
    const disabilityPolicyCoverage = policyCoverages?.[product] as ShortTermDisabilityPolicyCoverage;
    const hospitalIndemnityPolicyCoverage = policyCoverages?.[FederationProduct.HOSPITAL_INDEMNITY_CHOICE]?.coverages?.[0] as HospitalIndemnityPolicyCoveragePlan;
    const accidentPolicyCoverage = policyCoverages?.[FederationProduct.ACCIDENT] as AccidentPolicyCoverage
    const workStateIsMassachusetts = values.workState === StateAbbreviation.Ma;
    const isPfml = values.pfml;


    useEffect(() => {
        if(!disabilityPolicyCoverage) {
            return;
        }
        const employerContributoryPercentage = disabilityPolicyCoverage?.configuration?.employerContributoryPercentage;
        if (employerContributoryPercentage) {
            setFieldValue(`${product}EmployeeContributionPercentage`, (100 - employerContributoryPercentage).toString());
        }
        if(disabilityPolicyCoverage?.isBonusUp) {
            setFieldValue(`${product}PremiumTaxation`, Taxation.POST_TAX);
        }
    }, [setFieldValue, disabilityPolicyCoverage, product]);

    useEffect(() => {
        if(!hospitalIndemnityPolicyCoverage) {
            return;
        }
        const employerContributoryPercentage = hospitalIndemnityPolicyCoverage?.configuration?.employerContributoryPercentage;
        if (employerContributoryPercentage) {
            setFieldValue('hospitalIndemnityEmployeeContributionPercentage', (100 - employerContributoryPercentage).toString());
        }
    }, [setFieldValue, hospitalIndemnityPolicyCoverage]);

    useEffect(() => {
        if(!accidentPolicyCoverage) {
            return;
        }
        const employerContributoryPercentage = accidentPolicyCoverage?.configuration?.employerContributoryPercentage;
        if (employerContributoryPercentage) {
            setFieldValue(`accidentEmployeeContributionPercentage`, (100 - employerContributoryPercentage).toString());
        }
    }, [setFieldValue, accidentPolicyCoverage]);

    useEffect(() => {
        if(!isPfml || workStateIsMassachusetts) {
            return;
        }
        const rate = (values as PfmlEmployerFormValues).paidFamilyLeaveRate;
        const premiumTaxation = (values as PfmlEmployerFormValues).paidFamilyLeavePremiumTaxation;
        const premiumsAddedToEmployeesWages = (values as PfmlEmployerFormValues).paidFamilyLeavePremiumsAddedToEmployeesWages;
        if(rate){
            setFieldValue(`paidMedicalLeaveRate`, rate);
        }
        if (premiumTaxation) {
            setFieldValue(`paidMedicalLeavePremiumTaxation`, premiumTaxation);
        }
        if (premiumsAddedToEmployeesWages) {
            setFieldValue(`paidMedicalLeavePremiumsAddedToEmployeesWages`, premiumsAddedToEmployeesWages);
        }
    }, [setFieldValue, values, workStateIsMassachusetts, isPfml]);

    return (
        <QuestionPanel headerText="Tell us about your employee's insurance premiums."
                       detailsText="This information helps determine the taxability of your employee's benefits."
                       panelContent={<>
                           <div>
                               {values.disability && buildTaxationQuestions(getKeyByValue(FederationProduct, product)?.replaceAll("_", "-")?.toLowerCase())}
                               {values.hospitalIndemnity && buildTaxationQuestions("hospital-indemnity")}
                               {values.accident && buildTaxationQuestions("accident")}
                               {values.criticalIllness && buildTaxationQuestions("critical-illness")}
                               {findPaidFamilyMedicalLeaveTaxationQuestions()}

                               {Relationship.EMPLOYEE === values.relationship &&
                                   <>
                                       <RadioGroup id="premiums-paid-through-death"
                                                   label="Were premiums paid through the date of death?"
                                                   required={true} radioProps={[
                                           { id: "yes", text: "Yes" },
                                           { id: "no", text: "No" }
                                       ]}/>
                                       {(values as any as LifeEmployerFormValues)?.premiumsPaidThroughDeath === "No" &&
                                           <DateInput id='date-premiums-paid-through'
                                                      label="What date were premiums paid through?"
                                                      required={true}/>}
                                   </>}
                           </div>
                       </>}>
        </QuestionPanel>
    )

    function findPaidFamilyMedicalLeaveTaxationQuestions() {
        const isEmployee = values.genericRelationship === GenericRelationship.EMPLOYEE
        if (!isPfml) {
            return undefined
        }
        if (workStateIsMassachusetts) {
            if (isEmployee) {
                if ((values as any).conditionType === ConditionType.PREGNANCY || [LeaveType.MATERNITY_LEAVE, LeaveType.SERIOUS_HEALTH_CONDITION].includes((values as any).leaveType)) {
                    return   <>
                        {buildTaxationQuestions("paid-family-leave")}
                        {buildTaxationQuestions("paid-medical-leave")}
                    </>
                }
                return buildTaxationQuestions("paid-medical-leave")
            }
            return buildTaxationQuestions("paid-family-leave")
        }
        return buildTaxationQuestions("paid-family-medical-leave")
    }

    function buildTaxationQuestions(kebabProduct: string) {
        if(!kebabProduct) {
            return;
        }
        const newKebabProduct = workStateIsMassachusetts? kebabProduct : kebabProduct.replace('-medical', '');
        const lowerCaseProduct = kebabProduct.replaceAll('-', ' ');
        const camelCaseProduct = _.camelCase(newKebabProduct);
        const employeeContributionPercentage = _.get(values, `${camelCaseProduct}EmployeeContributionPercentage`) as any;
        const contributionValue = parseFloat(employeeContributionPercentage);
        const rate = _.get(values, `${camelCaseProduct}Rate`) as any;
        const rateValue = parseFloat(rate);

        return <>
            <RateInput id={`${newKebabProduct}-rate`}
                       label={`Employee portion of the Principal ${lowerCaseProduct} rate`}
                       required={true} />
            <PercentageInput id={`${newKebabProduct}-employee-contribution-percentage`}
                                              label={`Percent of ${lowerCaseProduct} premium paid by employee as of ${wageAsOfDate}`}
                                              required={true}/>
            {(contributionValue > 0 || !!rateValue)
                && <RadioGroup id={`${newKebabProduct}-premium-taxation`}
                               label={`Is your employee's portion of ${lowerCaseProduct} premium paid:`}
                               required={true}
                               radioProps={[{id: "pre-tax", text: Taxation.PRE_TAX}, {
                                   id: "post-tax",
                                   text: Taxation.POST_TAX
                               }]}/>}
            {(isPfml  || contributionValue < 100)
                && <RadioGroup id={`${newKebabProduct}-premiums-added-to-employees-wages`}
                               label={`Is the employer's portion of the ${lowerCaseProduct} premium added to the employees' wages (and reported as taxable income on their W-2) to allow for 100% of the premium to be paid post tax, and therefore the benefit is not taxable? (this is uncommon)`}
                               required={true} radioProps={[
                    { id: "yes", text: "Yes" },
                    { id: "no", text: "No" }
                ]}/>}
        </>
    }
};

export default Premiums;